import React from 'react';
import { BiErrorCircle } from 'react-icons/bi';
import { FaCircleChevronRight } from 'react-icons/fa6';

import { Popover, Tooltip } from 'antd';
import type { EditableSelectOption } from 'components/common/EditableCell';
import LinkButton from 'components/common/LinkButton/LinkButton';
import NumericInputField from 'components/common/NumericInputField';
import {
  TASKS_GRID_COLLAPSE_STEPS_ARIA_LABEL,
  TASKS_GRID_EXPAND_STEPS_ARIA_LABEL,
  TASKS_GRID_TARGET_FIELD_LABEL,
  TASKS_GRID_TASK_ACTIONS_ARIA_LABEL,
  TASKS_GRID_WATER_RATE_UNIT_LABEL,
  TASKS_SHEET_EDIT_TASK_ARIA_LABEL,
  TASKS_SHEET_NO_DATE,
  TASKS_SHEET_USE_WATER_RATE,
  TASKS_SHEET_WATER_LABEL,
  TASKS_SHEET_WATER_RATE_DEFAULT_TAG,
  TASKS_SHEET_WATER_RATE_DEFAULT_TOOLTIP,
  TASKS_SHEET_WATER_RATE_NEEDED,
  TASKS_SHEET_WATER_RATE_PLACEHOLDER,
} from 'constants/uiConstants';
import dayjs from 'dayjs';
import type { GUnit } from 'interfaces/IGlobalCatalogue';
import type { IInput } from 'interfaces/IInput';
import {
  getInputTaskTypeLabel,
  IInputTask,
  IInputTaskBase,
} from 'interfaces/IInputTask';
import MergeClassNames from 'utils/MergeClassNames';

import { isApplicationLikeTask, taskNeedsWaterRate } from './stepCalculations';
import { ApplicationTaskFields } from './taskFactory';
import { RowKebabButton } from './TaskRowContextMenu';
import TaskSheetDetailsForm from './TaskSheetDetailsForm';
import type { SheetTaskCellVisibility } from './tasksViewPreferences';
import TargetSpeciesPicker from './TargetSpeciesPicker';
import type { ContextMenuTarget } from './useStepContextMenu';

// Short, so the Task cell stays compact: "12 Apr 2026" or "Apr 2026".
const formatSheetDate = (task: IInputTask): string => {
  if (!task.endDate) return TASKS_SHEET_NO_DATE;
  const date = dayjs(task.endDate);
  if (!date.isValid()) return TASKS_SHEET_NO_DATE;
  return date.format(task.endDateHasDay ? 'D MMM YYYY' : 'MMM YYYY');
};

interface TaskSheetTaskCellProps {
  task: IInputTask;
  rowSpan: number;
  /** Set when stacked: the cell is a band across the product columns. */
  colSpan?: number;
  gUnits: GUnit[];
  planInput: IInput;
  areaUnitOptions: EditableSelectOption[];
  /** What the cell shows besides the task type (View options). */
  fields: SheetTaskCellVisibility;
  /** The narrow Task column: no area, and the fields stacked full width. */
  isTrimmed: boolean;
  /**
   * Compact tasks (the user's « choice): the type, then area, date and
   * timing on a second line that wraps, and a "Water rate needed" note if
   * one is due. Water rate and Target move to the popover.
   */
  isCompact: boolean;
  /** "+ Add input" stays in view (no inputs yet, or being entered); otherwise it shows on hover of the task's rows. */
  isAddControlPinned: boolean;
  /** The organisation's default water rate; a match is tagged "default". */
  orgDefaultWaterRate: number | null;
  /** Offered as "Use … L/ha" when the task needs a water rate and has none. */
  suggestedWaterRate: number | null;
  /** A task in the "Tasks without …" group: one line, greyed. */
  isMuted?: boolean;
  /**
   * Actual mode: the cell is charcoal with light text, with the header, so
   * it's unmistakable which of Plan and Actual is being edited (OP-2556).
   */
  isDark?: boolean;
  /** Set for a task with product rows: its chevron folds them to one line. */
  isCollapsed?: boolean;
  onToggleCollapsed?: () => void;
  isDetailsOpen: boolean;
  onDetailsOpenChange: (open: boolean) => void;
  isMenuOpen: boolean;
  /** The task's "+ Add input". */
  addControl?: React.ReactNode;
  /** Notes rows are hidden (View options), so the popover offers Notes. */
  areNotesHidden: boolean;
  /** Set for the task being entered: the popover shows every field and an "Add inputs" button. */
  onAddInputs?: () => void;
  commitTaskField: (
    task: IInputTask,
    patch: Partial<IInputTaskBase & ApplicationTaskFields>
  ) => void;
  onDateEditEnd?: (taskId: string) => void;
  onContextMenu: (event: React.MouseEvent, taskId: string) => void;
  onOpenMenuFromKebab: (
    event: React.MouseEvent<HTMLButtonElement>,
    target: ContextMenuTarget
  ) => void;
}

// The Task cell of the category sheet (OP-2556): spans the task's rows. It
// names the task, its area, date and timing (click to edit them), then keeps
// Water rate and Target in view and editable in place, since they're easy to
// miss behind a click, and ends with "+ Add input" and "+ Note". Trimmed, it
// drops the area and stacks the rest to fit, so nothing hides. View options
// choose which of the fields show.
const TaskSheetTaskCell: React.FC<TaskSheetTaskCellProps> = ({
  task,
  rowSpan,
  colSpan,
  gUnits,
  planInput,
  areaUnitOptions,
  fields,
  isTrimmed,
  isCompact,
  isAddControlPinned,
  orgDefaultWaterRate,
  suggestedWaterRate,
  isMuted = false,
  isDark = false,
  isCollapsed,
  onToggleCollapsed,
  isDetailsOpen,
  onDetailsOpenChange,
  isMenuOpen,
  addControl,
  areNotesHidden,
  onAddInputs,
  commitTaskField,
  onDateEditEnd,
  onContextMenu,
  onOpenMenuFromKebab,
}) => {
  const typeLabel = getInputTaskTypeLabel(task.taskType);
  const area =
    fields.area && task.area !== null
      ? `${task.area} ${task.areaUnit?.unit ?? ''}`.trim()
      : null;
  const whenLine = [
    fields.date ? formatSheetDate(task) : null,
    fields.timing ? task.timing?.item : null,
  ]
    .filter(Boolean)
    .join(' · ');
  // Compact, the area joins the date and timing on the second line.
  const secondLine = isCompact
    ? [area, whenLine].filter(Boolean).join(' · ')
    : whenLine;
  const applicationTask = isApplicationLikeTask(task) ? task : null;
  const isWaterRateNeeded =
    !!applicationTask &&
    applicationTask.waterRate === null &&
    taskNeedsWaterRate(task, gUnits);
  const showWaterRate = !!applicationTask && fields.waterRate;
  const isOrgDefault =
    !!applicationTask &&
    orgDefaultWaterRate !== null &&
    applicationTask.waterRate === orgDefaultWaterRate;
  const showTarget = !!applicationTask && fields.target;

  return (
    <td
      rowSpan={rowSpan}
      colSpan={colSpan}
      className={MergeClassNames(
        'task-sheet-task-cell group border border-gray-medium p-0 align-top',
        isMuted ? 'bg-rowHover' : isDark ? 'bg-charcoal' : 'bg-white'
      )}
      onContextMenu={(event) => onContextMenu(event, task.taskId)}
    >
      <div className="flex min-w-0 items-start gap-1 px-1 py-1">
        {isCollapsed !== undefined && (
          <button
            type="button"
            aria-label={
              isCollapsed
                ? TASKS_GRID_EXPAND_STEPS_ARIA_LABEL
                : TASKS_GRID_COLLAPSE_STEPS_ARIA_LABEL
            }
            aria-expanded={!isCollapsed}
            className={MergeClassNames(
              'mt-1 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded',
              isDark ? 'hover:bg-white/10' : 'hover:bg-gray-bright'
            )}
            onClick={onToggleCollapsed}
          >
            <FaCircleChevronRight
              size={14}
              aria-hidden="true"
              className={MergeClassNames(
                'transition-transform duration-150',
                isDark ? 'text-gray-bright' : 'text-secondary-dark',
                isCollapsed ? 'rotate-0' : 'rotate-90'
              )}
            />
          </button>
        )}
        <div className="flex min-w-0 flex-1 flex-col items-start gap-1">
          <Popover
            trigger="click"
            placement="bottomLeft"
            destroyOnHidden
            getPopupContainer={() => document.body}
            open={isDetailsOpen}
            onOpenChange={onDetailsOpenChange}
            classNames={{ root: 'task-sheet-details-popover' }}
            content={
              <TaskSheetDetailsForm
                task={task}
                gUnits={gUnits}
                selectedInputStartDate={planInput.startDate}
                areaUnitOptions={areaUnitOptions}
                commitTaskField={commitTaskField}
                onDateEditEnd={onDateEditEnd}
                showWaterRate={!fields.waterRate || isCompact}
                showTarget={!fields.target || isCompact}
                showNotes={areNotesHidden}
                onAddInputs={onAddInputs}
              />
            }
          >
            <button
              type="button"
              aria-label={`${TASKS_SHEET_EDIT_TASK_ARIA_LABEL}: ${typeLabel}`}
              aria-expanded={isDetailsOpen}
              className={MergeClassNames(
                'flex w-full min-w-0 flex-col rounded px-1 py-0.5 text-left transition-colors',
                isDark ? 'hover:bg-white/10' : 'hover:bg-gray-bright',
                isDetailsOpen && (isDark ? 'bg-white/10' : 'bg-gray-bright')
              )}
            >
              <span
                className={MergeClassNames(
                  'flex w-full min-w-0 items-center gap-1 text-sm',
                  isDark ? 'text-white' : 'text-charcoal'
                )}
              >
                <span className="min-w-0 truncate">
                  <span className="font-semibold">{typeLabel}</span>
                  {!isTrimmed && !isCompact && area && (
                    <span
                      className={isDark ? 'text-gray-bright' : 'text-gray-dark'}
                    >
                      {' '}
                      · {area}
                    </span>
                  )}
                  {isMuted && whenLine && (
                    <span className="text-sm text-gray-muted">
                      {' '}
                      · {whenLine}
                    </span>
                  )}
                </span>
              </span>
              {!isMuted && secondLine && (
                // Wraps rather than truncating, so a long timing reads whole.
                <span
                  className={MergeClassNames(
                    'min-w-0 break-words text-sm',
                    isDark ? 'text-gray-bright' : 'text-gray-muted'
                  )}
                >
                  {secondLine}
                </span>
              )}
            </button>
          </Popover>

          {isCompact && showWaterRate && isWaterRateNeeded && (
            <span
              className={MergeClassNames(
                'inline-flex items-center gap-1 px-1 text-sm',
                isDark ? 'text-danger-light' : 'text-danger-dark'
              )}
            >
              <BiErrorCircle aria-hidden="true" />
              {TASKS_SHEET_WATER_RATE_NEEDED}
            </span>
          )}

          {!isMuted &&
            !isCompact &&
            applicationTask &&
            (showWaterRate || showTarget) && (
              // The application's details in their own light box, labels on
              // the left and values in bold, so a set value stands out and a
              // missing one is plain to see.
              <div className="grid w-full grid-cols-[auto_minmax(0,1fr)] items-center gap-x-2 gap-y-1 rounded bg-rowHover px-2 py-1">
                {showWaterRate && (
                  <>
                    <span className="text-sm text-gray-muted">
                      {TASKS_SHEET_WATER_LABEL}
                    </span>
                    <span className="flex min-w-0 flex-wrap items-center gap-1">
                      <NumericInputField
                        unit={TASKS_GRID_WATER_RATE_UNIT_LABEL}
                        inputClassName="h-6 w-16 rounded bg-white text-sm font-semibold text-charcoal"
                        min={0}
                        allowEmpty
                        value={applicationTask.waterRate}
                        placeholder={TASKS_SHEET_WATER_RATE_PLACEHOLDER}
                        onCommit={(waterRate) =>
                          commitTaskField(task, { waterRate })
                        }
                      />
                      {isOrgDefault && (
                        <Tooltip title={TASKS_SHEET_WATER_RATE_DEFAULT_TOOLTIP}>
                          <span className="rounded border border-gray-medium px-1 text-xs text-gray-dark">
                            {TASKS_SHEET_WATER_RATE_DEFAULT_TAG}
                          </span>
                        </Tooltip>
                      )}
                      {isWaterRateNeeded && (
                        <span className="inline-flex items-center gap-1 text-sm text-danger-dark">
                          <BiErrorCircle aria-hidden="true" />
                          {TASKS_SHEET_WATER_RATE_NEEDED}
                        </span>
                      )}
                      {isWaterRateNeeded && suggestedWaterRate !== null && (
                        <LinkButton
                          onClick={() =>
                            commitTaskField(task, {
                              waterRate: suggestedWaterRate,
                            })
                          }
                        >
                          {TASKS_SHEET_USE_WATER_RATE(suggestedWaterRate)}
                        </LinkButton>
                      )}
                    </span>
                  </>
                )}
                {showTarget && (
                  <>
                    <span className="text-sm text-gray-muted">
                      {TASKS_GRID_TARGET_FIELD_LABEL}
                    </span>
                    <TargetSpeciesPicker
                      value={applicationTask.targetSpecies}
                      onCommit={(items) =>
                        commitTaskField(task, { targetSpecies: items })
                      }
                    />
                  </>
                )}
              </div>
            )}

          {!isMuted && addControl && (
            // Shown on hover or focus anywhere in the task's rows (its
            // <tbody> is group/task), so the grid stays quiet; it keeps its
            // space so nothing shifts as it appears.
            <div
              className={MergeClassNames(
                'flex flex-wrap items-center gap-1 px-1 transition-opacity',
                !isAddControlPinned &&
                  'opacity-0 focus-within:opacity-100 group-hover/task:opacity-100'
              )}
            >
              {addControl}
            </div>
          )}
        </div>
        <div className="flex flex-shrink-0 items-center pt-0.5">
          <RowKebabButton
            label={TASKS_GRID_TASK_ACTIONS_ARIA_LABEL}
            isActive={isMenuOpen}
            isOnDark={isDark}
            onOpen={(event) =>
              onOpenMenuFromKebab(event, {
                rowType: 'task',
                taskId: task.taskId,
              })
            }
          />
        </div>
      </div>
    </td>
  );
};

export default TaskSheetTaskCell;
